/**
 * API endpoint URLs and constants
 */

// Imported from shared package — import (not just re-export) so BLUESKY_CDN can reference these directly
import {
    AT_EXPLORE_BASE_URL,
    AT_EXPLORE_ENDPOINTS,
    feedThumbnail as sharedFeedThumbnail,
    feedFullsize as sharedFeedFullsize,
} from '@mackinac/shared'
export { AT_EXPLORE_BASE_URL, AT_EXPLORE_ENDPOINTS }

// Bluesky public API (unauthenticated read access)
export const BLUESKY_PUBLIC_API = 'https://public.api.bsky.app'
// Bluesky AppView DID — used when minting service-auth tokens for direct appview fetches
export const BLUESKY_APPVIEW_DID = 'did:web:api.bsky.app'

// Discover feed (Bluesky's "What's Hot" generator) — special-cased for feed-feedback
// eligibility to match the Bluesky reference client (it never sets acceptsInteractions).
export const DISCOVER_FEED_URI =
    'at://did:plc:z72i7hdynmk6r22z27h6tvur/app.bsky.feed.generator/whats-hot'

// Blacksky community app view (alternative AT Protocol indexer)
export const BLACKSKY_PUBLIC_API = 'https://api.blacksky.community'
// Blacksky AppView DID — used as the atproto-proxy target for service-auth reads
export const BLACKSKY_APPVIEW_DID = 'did:web:api.blacksky.community'

// Bluesky link card metadata service
export const BLUESKY_CARDYB_URL = 'https://cardyb.bsky.app'

// Mackinac XRPC server
// Override with VITE_MACKINAC_XRPC_URL in .env.local for local dev (e.g. '' to use the Vite proxy).
export const MACKINAC_XRPC_URL =
    import.meta.env.VITE_MACKINAC_XRPC_URL ?? 'https://api.mackinac.social'
export const MACKINAC_SERVICE_DID = 'did:web:api.mackinac.social'

function normalizeOverrideValue(value: string | undefined): string {
    return value?.trim() ?? ''
}

/**
 * Accepts only an origin: no userinfo, no path beyond "/" (or empty), no
 * query, no fragment. http is accepted only in dev (and only for
 * localhost/127.0.0.1); https is accepted for any host. IPv6 literals
 * (e.g. "http://[::1]:8795") are deliberately not treated as localhost —
 * `hostname` keeps the brackets ("[::1]"), which never equals "localhost"
 * or "127.0.0.1", so they fail safe rather than being special-cased.
 */
function parseOriginOnlyUrl(url: string, isDev: boolean): URL | null {
    let parsed: URL
    try {
        parsed = new URL(url)
    } catch {
        return null
    }
    if (parsed.username || parsed.password || parsed.search || parsed.hash) return null
    if (parsed.pathname !== '' && parsed.pathname !== '/') return null
    if (parsed.protocol === 'https:') return parsed
    return parsed.protocol === 'http:' &&
        isDev &&
        (parsed.hostname === 'localhost' || parsed.hostname === '127.0.0.1')
        ? parsed
        : null
}

/** did:web method-specific id for a host:port pair percent-encodes the port colon. */
function didWebHostFromUrl(parsed: URL): string {
    return parsed.host.replace(/:/g, '%3A')
}

/** Requires an exact-case "did:web:" prefix and a case-insensitive, exact host match. */
function isMatchingWebDid(did: string, parsed: URL): boolean {
    if (!did.startsWith('did:web:')) return false
    const host = did.slice('did:web:'.length)
    return host.toLowerCase() === didWebHostFromUrl(parsed).toLowerCase()
}

function validateOverride(
    url: string,
    did: string,
    isDev: boolean
): { apiUrl: string; serviceDid: string } | null {
    const parsed = parseOriginOnlyUrl(url, isDev)
    if (!parsed) return null
    if (!isMatchingWebDid(did, parsed)) return null
    return { apiUrl: parsed.origin, serviceDid: did }
}

/**
 * Resolves the Tahquamenon chat/skills backend, which is being relocated to
 * its own service (mackinac#2908). A token minted for one audience sent to
 * the other host is rejected by the server, so a half-configured or
 * untrustworthy override (only one var set, either malformed, or the DID's
 * host doesn't match the URL's host) must fail safe to the defaults rather
 * than mix an old token with a new host or vice versa.
 *
 * Pure: never logs. The caller inspects `overrideIgnored` and decides
 * whether to warn.
 */
export function resolveTahquamenonEndpoint(
    rawUrl: string | undefined,
    rawDid: string | undefined,
    defaults: { url: string; did: string },
    isDev: boolean
): { apiUrl: string; serviceDid: string; overrideIgnored: boolean } {
    const url = normalizeOverrideValue(rawUrl)
    const did = normalizeOverrideValue(rawDid)

    if (!url && !did) {
        return { apiUrl: defaults.url, serviceDid: defaults.did, overrideIgnored: false }
    }

    const validated = url && did ? validateOverride(url, did, isDev) : null
    if (!validated) {
        return { apiUrl: defaults.url, serviceDid: defaults.did, overrideIgnored: true }
    }
    return { ...validated, overrideIgnored: false }
}

// Tahquamenon chat/skills backend. Defaults to the standalone service;
// override both VITE_TAHQUAMENON_API_URL and VITE_TAHQUAMENON_SERVICE_DID
// to use another matching origin and service DID.
const tahquamenonEndpoint = resolveTahquamenonEndpoint(
    import.meta.env.VITE_TAHQUAMENON_API_URL,
    import.meta.env.VITE_TAHQUAMENON_SERVICE_DID,
    { url: 'https://api.whiteside.ai', did: 'did:web:api.whiteside.ai' },
    import.meta.env.DEV
)
if (tahquamenonEndpoint.overrideIgnored) {
    console.warn(
        'Tahquamenon endpoint override ignored: VITE_TAHQUAMENON_API_URL and VITE_TAHQUAMENON_SERVICE_DID must both be set to a matching origin and did:web'
    )
}
export const TAHQUAMENON_API_URL = tahquamenonEndpoint.apiUrl
export const TAHQUAMENON_SERVICE_DID = tahquamenonEndpoint.serviceDid

// Web Push VAPID public key (base64url). Not a secret; unset means push is not configured.
export const VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY as string | undefined

/**
 * AT-explore blob URL builders (replaces Bluesky CDN)
 */
export const BLUESKY_CDN = {
    avatarUrl: (did: string, blobRef: string) =>
        AT_EXPLORE_ENDPOINTS.atBlob(did, blobRef, 'avatar'),
    bannerUrl: (did: string, blobRef: string) =>
        AT_EXPLORE_ENDPOINTS.atBlob(did, blobRef, 'banner'),
    /** Feed image thumbnail via Bluesky CDN (provides server-side resizing) */
    feedThumbnail: sharedFeedThumbnail,
    /** Feed image fullsize via Bluesky CDN */
    feedFullsize: sharedFeedFullsize,
    /** Video thumbnail from Bluesky video service */
    videoThumbnail: (did: string, cid: string) =>
        `https://video.bsky.app/watch/${did}/${cid}/thumbnail.jpg`,
    /** Video playlist from Bluesky video service */
    videoPlaylist: (did: string, cid: string) =>
        `https://video.bsky.app/watch/${did}/${cid}/playlist.m3u8`,
} as const
